import * as Sentry from '@sentry/vue'
import getCurrentHost from '~/utils/api-utils'

export interface RegistrationCheckResult {
  emailExists: boolean
  phoneExists: boolean
}

/**
 * Checks CuDa's public registration endpoints to determine whether an email
 * or phone number is already associated with an existing customer account.
 *
 * isValid: false  →  the value is already in use (customer exists)
 * isValid: true   →  the value is available (new customer)
 *
 * CCT-2060
 */
export function useCudaRegistrationCheck() {
  const apiBase = 'api/customer/cuda/public/v3/customer/registration'

  async function checkEmail(email: string): Promise<boolean> {
    if (!email)
      return false
    try {
      const response = await $fetch<{ isValid: boolean }>(`${apiBase}/email-check`, {
        method: 'GET',
        query: { email },
        baseURL: getCurrentHost(),
      })
      // isValid: false means email is already taken → customer exists
      return !response.isValid
    }
    catch (error: unknown) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          flow: 'new-user',
          endpoint: 'cuda-registration-check',
          checkType: 'email',
        },
      })
      // On error we assume the user exists — safer than routing into the
      // new-user Keycloak registration path which would fail with a duplicate error
      return true
    }
  }

  async function checkPhone(phone: string): Promise<boolean> {
    if (!phone)
      return false
    try {
      const response = await $fetch<{ isValid: boolean }>(`${apiBase}/phone-check`, {
        method: 'GET',
        query: { phone },
        baseURL: getCurrentHost(),
      })
      // isValid: false means phone is already taken → customer exists
      return !response.isValid
    }
    catch (error: unknown) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          flow: 'new-user',
          endpoint: 'cuda-registration-check',
          checkType: 'phone',
        },
      })
      // Same conservative fallback as checkEmail — treat as existing on error
      return true
    }
  }

  async function checkRegistration(email: string, phone: string): Promise<RegistrationCheckResult> {
    const [emailExists, phoneExists] = await Promise.all([
      checkEmail(email),
      checkPhone(phone),
    ])
    return { emailExists, phoneExists }
  }

  return {
    checkRegistration,
  }
}
